import { connect } from 'formik';
import React, { Component, PropsWithChildren, createRef, RefObject } from 'react';
import { RegisterElement } from '..';
import { FormGroupContext } from '../FormGroup/FormGroup';
import '../NaturalFieldSelect/NaturalFieldSelect.scss';
import { resolveProps } from '../utils/formikUtils';
import { createId } from '../utils/IdGenerator';
import { InputField } from './InputField';
import './style.scss';
import { INaturalFieldTextProps, InnerNaturalFieldTextProps } from './types';

const CODE = {
  ENTER: 13,
  ESC: 27,
  SPACE: 32,
  TAB: 9,
};

export interface INaturalFieldTextState {
  expanded: boolean;
}

export class NaturalFieldText extends Component<PropsWithChildren<InnerNaturalFieldTextProps>, INaturalFieldTextState> {
  public static contextType = FormGroupContext;

  context: any;

  public readonly state: Readonly<INaturalFieldTextState> = {
    expanded: false,
  };

  private containerId = createId('nl-text');
  private linkContainer: RefObject<HTMLAnchorElement>;

  constructor(props: InnerNaturalFieldTextProps) {
    super(props);
    this.linkContainer = createRef();
  }

  public componentDidMount() {
    this.props.formik.registerField && this.props.formik.registerField(this.props.name, this);
    window.document.addEventListener('keydown', this.onEscape, true);
  }

  public componentDidUpdate() {
    const { touched, valid } = resolveProps(this.props, this.props.formik);
    const showError = touched && !valid;
    if (this.context !== null && showError !== this.context.formGroupErrors.get(this.props.name)) {
      this.context.updateFormGroupErrors(this.props.name, showError);
    }
  }

  public componentWillUnmount() {
    this.props.formik.unregisterField && this.props.formik.unregisterField(this.props.name);
    window.document.removeEventListener('keydown', this.onEscape, true);
  }

  public render() {
    const { expanded } = this.state;
    const id = this.containerId;
    const {
      align = 'left',
      color = '#444',
      name,
      placeholder = '?',
      title = 'Click to edit',
      errorMessage,
      required,
      valid = true,
      touched = false,
      value,
      fontSize,
    } = resolveProps(this.props, this.props.formik);

    const showError = touched && !valid;
    const showSuccess = touched && valid;

    const errorBorder = showError && errorMessage ? { border: '2px dashed #f44123', padding: '0 3px' } : {};

    const linkStyles = {
      borderBottomColor: color,
      color,
      fontSize,
      ...errorBorder,
      display: expanded ? 'none' : '',
    };

    return (
      <>
        <RegisterElement name={`register-${id}`}>
          {(ref: any) => {
            const myRef = showError ? ref : undefined;
            return (
              <div ref={myRef} id={`${id}-wrapper`} className="nl-container" qa-id={this.props['qa-id']}>
                <a
                  id={id}
                  className="nl-field-link"
                  style={linkStyles}
                  title={showError ? errorMessage : title}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.toggleExpanded();
                  }}
                  href="#"
                  onKeyDown={(e) => {
                    if (e.keyCode === CODE.ENTER || e.keyCode === CODE.SPACE) {
                      e.preventDefault();
                      this.toggleExpanded();
                    }
                  }}
                  aria-haspopup={true}
                  aria-expanded={expanded}
                  ref={this.linkContainer}
                >
                  {!!value && value}
                  {!value && <span>{placeholder}</span>}
                </a>
                {expanded && (
                  <InputField
                    name={name}
                    labeledById={this.containerId}
                    align={align}
                    fontSize={fontSize}
                    placeholder={placeholder}
                    value={value}
                    onBlur={() => {
                      const { formik } = this.props;
                      if (!formik) {
                        return;
                      }
                      const alreadyTouched = Boolean(formik.touched && formik.touched[name]);

                      if (!alreadyTouched) {
                        formik.setFieldTouched && formik.setFieldTouched(name);
                      }
                    }}
                    onChange={(x, inputstring) => {
                      const { onChange, formik } = this.props;

                      if (onChange) {
                        onChange(name, inputstring);
                      } else if (formik && formik.setFieldValue) {
                        formik.setFieldValue(name, inputstring);
                      }

                      this.toggleExpanded(false);
                    }}
                  />
                )}
              </div>
            );
          }}
        </RegisterElement>
        <div
          className="nl-overlay"
          style={{ display: expanded ? 'block' : 'none' }}
          aria-hidden={true}
          onClick={(e) => {
            e.stopPropagation();
          }}
          data-testid="nl-overlay"
        />
        <span className="sr-only" id={`${id}Status`}>
          {showError && '(error)'}
          {showSuccess && '(success)'}
        </span>
      </>
    );
  }

  private focus = () => {
    this.linkContainer.current && this.linkContainer.current.focus();
  };

  // toggle expanded or optionally force open/closed
  private toggleExpanded = (expanded?: boolean) => {
    this.setState(
      (prevState) => {
        expanded = expanded === undefined ? !prevState.expanded : expanded;
        return prevState.expanded !== expanded ? { expanded } : null;
      },
      () => {
        if (!expanded) {
          // need to pause for one cpu cycle so that linkContainer can accept focus
          // and so we can see the focus outline around the NaturalFieldText element
          setTimeout(this.focus, 0);
        }
      },
    );
  };
  private onEscape = (e: KeyboardEvent) => {
    if (this.state.expanded && e.keyCode === 27) {
      this.toggleExpanded(this.state.expanded);
      e.stopPropagation();
    }
  };
}

NaturalFieldText.contextType = FormGroupContext;

export default connect<INaturalFieldTextProps>(NaturalFieldText);
